import { createEffect, createMemo, Show, createSignal } from "solid-js";
import { useQueryClient } from "@tanstack/solid-query";
import { session, setTenantId, tenantId } from "./signals";
import { A, useLocation, useNavigate, useParams, useSearchParams } from "@solidjs/router";
import { Text } from "./i18n";
import { authClient, useSession } from "../lib/auth-client";
import LanguageSelector from "./LanguageSelector";
import type { TenantConfig } from "./TenantTypes";
import { OrganizationSwitcher } from "./OrganizationSwitcher";
import { useProjectPermissions } from "./hooks/useProjectPermissions";
import { FaSolidChevronDown, FaSolidUser, FaSolidRightFromBracket } from "solid-icons/fa";
import { urlPath } from "./urlUtils";

interface Props {
  config?: TenantConfig;
}

export const NavBar = (props: Props) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessionQuery = useSession();
  const [isStoppingImpersonation, setIsStoppingImpersonation] = createSignal(false);
  const { canCreate, canManageOrg } = useProjectPermissions();
  const isImpersonating = createMemo(() => {
    return !!sessionQuery()?.data?.session.impersonatedBy;
  });

  createEffect(() => {
    setTenantId(params.tenantId ?? "");
  });

  return (
    <div class="ui-navbar bg-base-100 shadow-md select-none">
      <div class="ui-navbar-start">
        <div class="ui-navbar-center gap-1">
          <A
            href={urlPath("/", (searchParams.tenantId as string | undefined) ?? tenantId())}
            end
            class="ui-btn ui-btn-ghost"
          >
            <Text message="Home" />
          </A>
          <Show
            when={
              tenantId() &&
              (props.config?.mode === "editor" || (props.config?.mode === "project" && canCreate())) &&
              session.user
            }
          >
            <A href={`/${tenantId()}/projects`} class="ui-btn ui-btn-ghost">
              <Text message="Projects" />
            </A>
          </Show>
          <Show when={tenantId() && canManageOrg() && session.user}>
            <A href={`/${tenantId()}/scenarios`} class="ui-btn ui-btn-ghost">
              <Text message="Scenarios" />
            </A>
            <A href={`/${tenantId()}/storage`} class="ui-btn ui-btn-ghost">
              <Text message="Storage" />
            </A>
            <A href={`/${tenantId()}/libraries`} class="ui-btn ui-btn-ghost">
              <Text message="Libraries" />
            </A>
          </Show>
        </div>
      </div>
      <div class="ui-navbar-end gap-2">
        <LanguageSelector />
        <Show when={!session.user && !location.pathname.includes("accept-invitation")}>
          <A href={urlPath("/login", tenantId())} class="ui-btn ui-btn-soft ui-btn-success">
            <Text message="Log In" />
          </A>
        </Show>
        <Show when={session.user}>
          <Show when={isImpersonating()}>
            <button
              class="ui-btn ui-btn-secondary ui-btn-sm"
              disabled={isStoppingImpersonation()}
              onClick={async () => {
                setIsStoppingImpersonation(true);
                await authClient.admin.stopImpersonating();
                setIsStoppingImpersonation(false);
                window.location.href = "/admin/users";
              }}
            >
              <Show when={isStoppingImpersonation()} fallback={<Text message="Stop Impersonation" />}>
                <span class="ui-loading ui-loading-spinner ui-loading-xs"></span>
              </Show>
            </button>
          </Show>
          <OrganizationSwitcher />
          <div class="ui-dropdown ui-dropdown-end">
            <div tabindex="0" role="button" class="ui-btn ui-btn-ghost gap-1">
              <Show
                when={session.user?.name}
                fallback={
                  <Show when={session.user?.email} fallback={<span>anon</span>}>
                    <span>{session.user?.email.split("@")[0]}</span>
                  </Show>
                }
              >
                <span>{session.user?.name}</span>
              </Show>
              <FaSolidChevronDown size={12} />
            </div>
            <ul
              tabindex="-1"
              class="ui-dropdown-content ui-menu bg-base-100 rounded-box border-base-200 z-10 w-52 border p-2 shadow-md"
            >
              <li>
                <button
                  type="button"
                  class="flex items-center gap-2"
                  onClick={() => {
                    // @ts-ignore
                    document.activeElement.blur();
                    navigate(urlPath("/profile", tenantId()));
                  }}
                >
                  <FaSolidUser size={14} class="text-base-content/60" />
                  <Text message="My profile" />
                </button>
              </li>
              <li>
                <button
                  type="button"
                  class="text-error hover:bg-error/10 flex items-center gap-2"
                  onClick={async () => {
                    await authClient.signOut();
                    queryClient.invalidateQueries({ queryKey: ["rooms"] });
                    queryClient.invalidateQueries({ queryKey: ["latest-rooms"] });
                    navigate(urlPath("/", tenantId()));
                  }}
                >
                  <FaSolidRightFromBracket size={14} />
                  <Text message="Sign Out" />
                </button>
              </li>
            </ul>
          </div>
        </Show>
      </div>
    </div>
  );
};
